import * as actions from 'appRedux/actions/taxInvoice/vatInfoActionType';

export interface VatInfoState {
  getVatInfo: {
    loader: boolean;
    data: actions.GetVatInfoResponse | null;
    error: string | null;
    requestPayload: actions.GetVatInfoRequest | null;
    process: {
      status: boolean;
      action: string;
      message: string;
    };
  };
  issueTaxInvoice: {
    loader: boolean;
    data: actions.IssueTaxInvoiceResponse | null;
    error: string | null;
    process: {
      status: boolean;
      action: string;
      message: string;
    };
  };
  cancelTaxInvoice: {
    loader: boolean;
    data: actions.CancelTaxInvoiceResponse | null;
    error: string | null;
    process: {
      status: boolean;
      action: string;
      message: string;
    };
  };
  updateTaxInvoice: {
    loader: boolean;
    data: actions.UpdateTaxInvoiceResponse | null;
    error: string | null;
    process: {
      status: boolean;
      action: string;
      message: string;
    };
  };
  getActiveTaxInvoices: {
    loader: boolean;
    data: actions.GetActiveTaxInvoicesResponse | null;
    error: string | null;
    process: {
      status: boolean;
      action: string;
      message: string;
    };
  };
  verifyCreditNote: {
    loader: boolean;
    data: actions.GetVatInfoResponse | null;
    error: string | null;
    process: {
      status: boolean;
      action: string;
      message: string;
    };
  };
}

const initialState: VatInfoState = {
  getVatInfo: {
    loader: false,
    data: null,
    error: null,
    requestPayload: null,
    process: {
      status: false,
      action: '',
      message: '',
    },
  },
  issueTaxInvoice: {
    loader: false,
    data: null,
    error: null,
    process: {
      status: false,
      action: '',
      message: '',
    },
  },
  cancelTaxInvoice: {
    loader: false,
    data: null,
    error: null,
    process: {
      status: false,
      action: '',
      message: '',
    },
  },
  updateTaxInvoice: {
    loader: false,
    data: null,
    error: null,
    process: {
      status: false,
      action: '',
      message: '',
    },
  },
  getActiveTaxInvoices: {
    loader: false,
    data: null,
    error: null,
    process: {
      status: false,
      action: '',
      message: '',
    },
  },
  verifyCreditNote: {
    loader: false,
    data: null,
    error: null,
    process: {
      status: false,
      action: '',
      message: '',
    },
  },
};

export default (
  state: VatInfoState = initialState,
  action: actions.VatInfoAction
): VatInfoState => {
  switch (action.type) {
    // Get VAT Info Actions
    case actions.GET_VAT_INFO_REQUEST:
      return {
        ...state,
        getVatInfo: {
          ...state.getVatInfo,
          loader: true,
          error: null,
          requestPayload: action.payload,
          process: {
            status: false,
            action: 'getVatInfo',
            message: '',
          },
        },
      };

    case actions.GET_VAT_INFO_SUCCESS:
      return {
        ...state,
        getVatInfo: {
          ...state.getVatInfo,
          loader: false,
          data: action.payload,
          error: null,
          process: {
            status: true,
            action: 'getVatInfo',
            message: action.payload.data?.result_msg || 'Success',
          },
        },
      };

    case actions.GET_VAT_INFO_ERROR:
      return {
        ...state,
        getVatInfo: {
          ...state.getVatInfo,
          loader: false,
          error: action.payload.message,
          process: {
            status: false,
            action: 'getVatInfo',
            message: action.payload.message,
          },
        },
      };

    case actions.GET_VAT_INFO_RESET:
      return {
        ...state,
        getVatInfo: {
          ...initialState.getVatInfo,
        },
      };

    // Issue Tax Invoice Actions
    case actions.ISSUE_TAX_INVOICE_REQUEST:
      return {
        ...state,
        issueTaxInvoice: {
          ...state.issueTaxInvoice,
          loader: true,
          error: null,
          process: {
            status: false,
            action: 'issueTaxInvoice',
            message: '',
          },
        },
      };

    case actions.ISSUE_TAX_INVOICE_SUCCESS:
      // Check if this is actually an error response (result_code !== '0')
      const isErrorResponse = action.payload.data?.result_code !== undefined && action.payload.data?.result_code !== '0';
      return {
        ...state,
        issueTaxInvoice: {
          ...state.issueTaxInvoice,
          loader: false,
          data: action.payload,
          error: isErrorResponse ? action.payload.data?.result_msg : null,
          process: {
            status: !isErrorResponse, // status is false if error response
            action: 'issueTaxInvoice',
            message: action.payload.data?.result_msg || (isErrorResponse ? 'Error' : 'Success'),
          },
        },
      };

    case actions.ISSUE_TAX_INVOICE_ERROR:
      return {
        ...state,
        issueTaxInvoice: {
          ...state.issueTaxInvoice,
          loader: false,
          error: action.payload.message,
          process: {
            status: false,
            action: 'issueTaxInvoice',
            message: action.payload.message,
          },
        },
      };

    case actions.ISSUE_TAX_INVOICE_RESET:
      return {
        ...state,
        issueTaxInvoice: {
          ...initialState.issueTaxInvoice,
        },
      };

    // Cancel Tax Invoice Actions
    case actions.CANCEL_TAX_INVOICE_REQUEST:
      return {
        ...state,
        cancelTaxInvoice: {
          ...state.cancelTaxInvoice,
          loader: true,
          error: null,
          process: {
            status: false,
            action: 'cancelTaxInvoice',
            message: '',
          },
        },
      };

    case actions.CANCEL_TAX_INVOICE_SUCCESS:
      return {
        ...state,
        cancelTaxInvoice: {
          ...state.cancelTaxInvoice,
          loader: false,
          data: action.payload,
          error: null,
          process: {
            status: true,
            action: 'cancelTaxInvoice',
            message: action.payload.data?.result_msg || 'Success',
          },
        },
      };

    case actions.CANCEL_TAX_INVOICE_ERROR:
      return {
        ...state,
        cancelTaxInvoice: {
          ...state.cancelTaxInvoice,
          loader: false,
          error: action.payload.message,
          process: {
            status: false,
            action: 'cancelTaxInvoice',
            message: action.payload.message,
          },
        },
      };

    case actions.CANCEL_TAX_INVOICE_RESET:
      return {
        ...state,
        cancelTaxInvoice: {
          ...initialState.cancelTaxInvoice,
        },
      };

    // Update Tax Invoice Actions
    case actions.UPDATE_TAX_INVOICE_REQUEST:
      return {
        ...state,
        updateTaxInvoice: {
          ...state.updateTaxInvoice,
          loader: true,
          error: null,
          process: {
            status: false,
            action: 'updateTaxInvoice',
            message: '',
          },
        },
      };

    case actions.UPDATE_TAX_INVOICE_SUCCESS:
      return {
        ...state,
        updateTaxInvoice: {
          ...state.updateTaxInvoice,
          loader: false,
          data: action.payload,
          error: null,
          process: {
            status: true,
            action: 'updateTaxInvoice',
            message: action.payload.data?.result_msg || 'Success',
          },
        },
      };

    case actions.UPDATE_TAX_INVOICE_ERROR:
      return {
        ...state,
        updateTaxInvoice: {
          ...state.updateTaxInvoice,
          loader: false,
          error: action.payload.message,
          process: {
            status: false,
            action: 'updateTaxInvoice',
            message: action.payload.message,
          },
        },
      };

    case actions.UPDATE_TAX_INVOICE_RESET:
      return {
        ...state,
        updateTaxInvoice: {
          ...initialState.updateTaxInvoice,
        },
      };

    // Get Active Tax Invoices Actions
    case actions.GET_ACTIVE_TAX_INVOICES_REQUEST:
      return {
        ...state,
        getActiveTaxInvoices: {
          ...state.getActiveTaxInvoices,
          loader: true,
          error: null,
          process: {
            status: false,
            action: 'getActiveTaxInvoices',
            message: '',
          },
        },
      };

    case actions.GET_ACTIVE_TAX_INVOICES_SUCCESS:
      return {
        ...state,
        getActiveTaxInvoices: {
          ...state.getActiveTaxInvoices,
          loader: false,
          data: action.payload,
          error: null,
          process: {
            status: true,
            action: 'getActiveTaxInvoices',
            message: action.payload.data?.result_msg || 'Success',
          },
        },
      };

    case actions.GET_ACTIVE_TAX_INVOICES_ERROR:
      return {
        ...state,
        getActiveTaxInvoices: {
          ...state.getActiveTaxInvoices,
          loader: false,
          error: action.payload.message,
          process: {
            status: false,
            action: 'getActiveTaxInvoices',
            message: action.payload.message,
          },
        },
      };

    case actions.GET_ACTIVE_TAX_INVOICES_RESET:
      return {
        ...state,
        getActiveTaxInvoices: {
          ...initialState.getActiveTaxInvoices,
        },
      };

    // Verify Credit Note Actions
    case actions.VERIFY_CREDIT_NOTE_REQUEST:
      return {
        ...state,
        verifyCreditNote: {
          ...state.verifyCreditNote,
          loader: true,
          error: null,
          process: {
            status: false,
            action: 'verifyCreditNote',
            message: '',
          },
        },
      };

    case actions.VERIFY_CREDIT_NOTE_SUCCESS:
      return {
        ...state,
        verifyCreditNote: {
          ...state.verifyCreditNote,
          loader: false,
          data: action.payload,
          error: null,
          process: {
            status: true,
            action: 'verifyCreditNote',
            message: action.payload.data?.result_msg || 'Success',
          },
        },
      };

    case actions.VERIFY_CREDIT_NOTE_ERROR:
      return {
        ...state,
        verifyCreditNote: {
          ...state.verifyCreditNote,
          loader: false,
          error: action.payload.message,
          process: {
            status: false,
            action: 'verifyCreditNote',
            message: action.payload.message,
          },
        },
      };

    case actions.VERIFY_CREDIT_NOTE_RESET:
      return {
        ...state,
        verifyCreditNote: {
          ...initialState.verifyCreditNote,
        },
      };

    default:
      return state;
  }
};



